import { zodResolver } from "@hookform/resolvers/zod";
import { Close } from "@mui/icons-material";
import { Button, Stack, Typography } from "@mui/joy";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ApiLine } from "griptonite-api-client";
import omit from "lodash/omit";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm, useFormState } from "react-hook-form";
import { z } from "zod";

import {
  useCreateLineMutation,
  useDeleteLineMutation,
  useLineQuery,
  useUpdateLineMutation,
  useVenueRoutesByClimbTypeQuery,
  useVenueZonesByClimbType,
} from "@/hooks/routes";
import { useCurrentVenueUuid } from "@/hooks/venues";

import FormErrorAlert from "../FormErrorAlert";
import { LineMapPositionInput } from "../forms/MapPositionInput";
import { PillsSelectInput } from "../forms/PillsSelect";
import TextInput, { HeightInputAutoUnits, NumberInput } from "../forms/TextInput";
import { ToggleButtonSelectInput } from "../forms/ToggleButtonSelect";
import { IconButtonLink } from "../Links";
import { FormSection } from "../RouteForm/RouteFormFields";
import Sidebar, { SidebarHeader } from "../Sidebar";

const FormButtons = ({ line }: { line?: ApiLine }) => {
  const formState = useFormState<ApiLine>();
  const deleteMutation = useDeleteLineMutation();
  const navigate = useNavigate();
  const currentVenueUuid = useCurrentVenueUuid();
  const { data: routes } = useVenueRoutesByClimbTypeQuery(currentVenueUuid, "SPORT");
  const lineRoutes = routes?.filter((r) => r.startLine?.uuid === line?.uuid && !r.endTime);

  const handleDelete = async () => {
    if (formState.isSubmitting || !line || !lineRoutes) return;
    if (lineRoutes.length > 0) {
      alert("This line has active routes. Please strip the routes before deleting the line.");
      return;
    }
    await deleteMutation.mutateAsync(line.uuid, {
      onSuccess: () => {
        navigate({
          from: "/gym/$venueUuid",
          to: "/gym/$venueUuid/settings/lines",
          params: true,
        });
      },
    });
  };

  let saveText = "Save";
  if (formState.isSubmitting) {
    saveText = "Saving...";
  }
  if (formState.isDirty) {
    return (
      <Stack
        sx={{
          position: "sticky",
          bottom: "0px",
          padding: 1,
          zIndex: 10,
          backgroundColor: "white",
        }}
        spacing={1}
      >
        <Button
          sx={{ flex: 1 }}
          type="submit"
          loading={formState.isSubmitting}
          loadingPosition="start"
        >
          {saveText}
        </Button>
        <Button sx={{ flex: 1 }} type="button" color="danger" onClick={handleDelete}>
          Delete
        </Button>
      </Stack>
    );
  } else if (line) {
    return (
      <Stack
        sx={{
          position: "sticky",
          bottom: "0px",
          padding: 1,
          zIndex: 10,
          backgroundColor: "white",
        }}
        direction="row"
        spacing={1}
      >
        <Button sx={{ flex: 1 }} type="button" color="danger" onClick={handleDelete}>
          Delete
        </Button>
      </Stack>
    );
  }
  return null;
};

const LineSchema = z.object({
  name: z.string(),
  height: z.number().nonnegative(),
  quickdrawCount: z.number({ message: "Required" }).int().nonnegative(),
  ascentStyles: z.array(z.enum(["LEAD", "TOPROPE", "AUTO_BELAY"])).min(1, "Required"),
  zone: z.string().uuid().nullable(),
  mapPosition: z.object({
    x: z.number(),
    y: z.number(),
  }),
  map: z.string(),
});

type LineFormValues = z.infer<typeof LineSchema>;

const lineToFormValues = (line: ApiLine | undefined): Partial<LineFormValues> => {
  return {
    name: line?.name || "",
    height: line?.height,
    quickdrawCount: line?.quickdrawCount ?? undefined,
    ascentStyles: line
      ? [
          ...(line.canLead ? (["LEAD"] as const) : []),
          ...(line.canToprope ? (["TOPROPE"] as const) : []),
          ...(line.autoBelay ? (["AUTO_BELAY"] as const) : []),
        ]
      : ["TOPROPE", "LEAD"],
    zone: line?.zone?.uuid ?? null,
    mapPosition: line?.mapPosition,
    map: line?.map,
  };
};

const LineForm = ({ line }: { line?: ApiLine }) => {
  const currentVenueUuid = useCurrentVenueUuid();
  const { data: zones } = useVenueZonesByClimbType(currentVenueUuid, "SPORT");

  const defaultValues = useMemo(() => lineToFormValues(line), [line]);

  const methods = useForm<LineFormValues>({
    defaultValues,
    resolver: zodResolver(LineSchema),
    mode: "onBlur",
  });

  methods.watch("ascentStyles");
  useEffect(() => {
    methods.reset(lineToFormValues(line));
  }, [line, methods]);

  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const updateMutation = useUpdateLineMutation();
  const createMutation = useCreateLineMutation();
  const isEditing = !!line;
  const mutation = isEditing ? updateMutation : createMutation;

  const onSubmit = async (values: LineFormValues) => {
    await mutation.mutateAsync(
      {
        ...omit(values, ["ascentStyles"]),
        canLead: values.ascentStyles.includes("LEAD"),
        canToprope: values.ascentStyles.includes("TOPROPE"),
        autoBelay: values.ascentStyles.includes("AUTO_BELAY"),
        height: Math.round(values.height),
        // @ts-expect-error Fix mutation union usage here
        uuid: line?.uuid,
        ...(!line ? { venue: currentVenueUuid } : {}),
      },
      {
        onSuccess: (line) => {
          if (currentVenueUuid) {
            queryClient.invalidateQueries({
              queryKey: ["venues", currentVenueUuid, "routes"],
            });
          }
          navigate({
            from: "/gym/$venueUuid",
            to: "/gym/$venueUuid/settings/lines/$lineUuid",
            params: (prev) => ({ ...prev, lineUuid: line.uuid }),
            replace: true,
          });
        },
        onError: (error) => {
          console.error(error);
        },
      },
    );
  };

  return (
    <FormProvider {...methods}>
      <LineMapPositionInput line={line} />
      <Stack
        spacing={1}
        sx={{
          paddingTop: 1,
          fontSize: 14,
          flex: 1,
          overflow: "auto",
          position: "relative",
        }}
        component="form"
        autoComplete="off"
        onSubmit={methods.handleSubmit(onSubmit)}
        onReset={() => {
          methods.reset();
        }}
      >
        <Stack sx={{ flex: 1 }}>
          <FormErrorAlert />
          <FormSection>
            <TextInput name="name" label="Name" />
          </FormSection>
          {/* <FormSection>
            <CheckboxInput name="topropeOnly" label="Toprope only?" />
          </FormSection>
          <FormSection>
            <CheckboxInput name="autoBelay" label="Has autobelay?" />
          </FormSection> */}
          <FormSection>
            <ToggleButtonSelectInput
              name="ascentStyles"
              label="Ascent style"
              options={[
                { name: "Lead", value: "LEAD" },
                { name: "Top rope", value: "TOPROPE" },
                { name: "Auto belay", value: "AUTO_BELAY" },
              ]}
            />
          </FormSection>
          <FormSection>
            <NumberInput name="quickdrawCount" label="Quickdraws" />
          </FormSection>
          <FormSection>
            <HeightInputAutoUnits name="height" label="Height" />
          </FormSection>
          <FormSection>
            <PillsSelectInput
              label="Sector"
              name="zone"
              options={
                zones?.map((zone) => ({
                  name: zone.name,
                  value: zone.uuid,
                })) || []
              }
            />
          </FormSection>
        </Stack>
        <FormButtons line={line} />
      </Stack>
    </FormProvider>
  );
};

export const LineSidebar = ({ uuid }: { uuid: string | null }) => {
  const currentVenueUuid = useCurrentVenueUuid();
  const { data: line } = useLineQuery(uuid ?? undefined, currentVenueUuid);

  return (
    <Sidebar
      sx={{
        borderLeft: "1px solid",
        borderColor: "var(--joy-palette-neutral-300)",
        borderTopLeftRadius: 16,
      }}
    >
      <SidebarHeader>
        <Typography level="title-md">{line?.name || "New Line"}</Typography>
        <CloseButton />
      </SidebarHeader>
      <LineForm line={line} />
    </Sidebar>
  );
};

const CloseButton = () => {
  return (
    <IconButtonLink to="/gym/$venueUuid/settings/lines" from="/gym/$venueUuid" params={true}>
      <Close />
    </IconButtonLink>
  );
};
